import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'

import styled from 'styled-components'

import { ViewpointFlatImage } from '../../../../api/viewpointFlatImages'
import iconBackDark from '../../../../assets/images/icon_back_dark.svg'
import iconCalendar from '../../../../assets/images/icon_calendar.svg'
import iconExpand from '../../../../assets/images/icon_expand.svg'
import iconForward from '../../../../assets/images/icon_forward.svg'
import { media } from '../../../../styles/breakpoints'
import { Icon } from '../../../common/Icon'
import { LoadingIndicator } from '../../../common/LoadingIndicator'

import { FlatImageUploadModal } from './FlatImageUploadModal'

/** Persists across panel unmount so reopening the same image skips the spinner. */
const loadedFlatImageKeys = new Set<string>()

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const PanelBase = styled.div`
  position: absolute;
  right: 0;
  top: 24px;
  width: 400px;
  filter: drop-shadow(0px 5px 30px rgba(0, 0, 0, 0.25));
  z-index: 100;
  pointer-events: auto;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 2px;
  overflow: hidden;

  &:fullscreen {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    max-width: none;
    border-radius: 0;
    filter: none;
  }

  @media ${media.belowTablet} {
    position: static;
    width: 100%;
    max-width: none;
    top: auto;
    right: auto;
    margin-top: 12px;
    filter: drop-shadow(0px 2px 12px rgba(0, 0, 0, 0.12));
  }
`

const PanelHeader = styled.div`
  position: relative;
  z-index: 3;
  height: 40px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  color: #7e858e;
  padding: 0 12px 0 16px;
  flex-shrink: 0;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`

const HeaderIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  border-radius: 2px;

  &:hover:not(:disabled) {
    background: #f0f4f8;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  img {
    display: block;
    width: 16px;
    height: 16px;
  }
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #7e858e;
  padding: 0 4px;
  border-radius: 2px;

  &:hover {
    color: #333;
    background: #f0f4f8;
  }
`

const PanelFooter = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #e8eaed;
  flex-shrink: 0;
`

const FooterButton = styled.button`
  padding: 7px 14px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 2px;
  border: 1px solid #073c7a;
  background: #fff;
  color: #073c7a;
  cursor: pointer;

  &:hover {
    background: #f0f4f8;
  }
`

const Viewport = styled.div`
  position: relative;
  z-index: 1;
  height: 320px;
  flex: 1;
  min-height: 200px;
  background: #1a1a1a;
  overflow: hidden;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  ${PanelBase}:fullscreen & {
    height: auto;
  }
`

const ViewportLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
`

const ViewportImage = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  max-width: none;
  user-select: none;
  pointer-events: none;
`

const EmptyState = styled.div`
  position: relative;
  z-index: 2;
  height: 220px;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #7e858e;
  font-size: 15px;
`

interface ImageTransform {
  scale: number
  offsetX: number
  offsetY: number
}

const DEFAULT_TRANSFORM: ImageTransform = {
  scale: 1,
  offsetX: 0,
  offsetY: 0,
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const stopPanelEvent = (e: React.SyntheticEvent) => {
  e.stopPropagation()
}

interface FlatImagePanelProps {
  projectId: string
  floorCode: string
  subPointId: number
  flatImage: ViewpointFlatImage | null
  flatImages?: ViewpointFlatImage[]
  flatImageIndex?: number
  onSelectFlatImageIndex?: (index: number) => void
  loading?: boolean
  onClose: () => void
  onUploadSuccess: () => void
}

export const FlatImagePanel = ({
  projectId,
  floorCode,
  subPointId,
  flatImage,
  flatImages = [],
  flatImageIndex = 0,
  onSelectFlatImageIndex,
  loading,
  onClose,
  onUploadSuccess,
}: FlatImagePanelProps) => {
  const imageUrl = flatImage?.image_url ?? null
  const imageCacheKey = flatImage?.image_s3_key ?? imageUrl ?? ''

  const [transform, setTransform] = useState<ImageTransform>(DEFAULT_TRANSFORM)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(
    () => !!imageCacheKey && loadedFlatImageKeys.has(imageCacheKey),
  )
  const [isFullscreen, setIsFullscreen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const previousCacheKeyRef = useRef<string>('')
  const dragRef = useRef<{
    startX: number
    startY: number
    originOffsetX: number
    originOffsetY: number
  } | null>(null)

  useEffect(() => {
    if (!imageCacheKey) {
      setImageLoaded(false)
      return
    }

    const isNewImage = previousCacheKeyRef.current !== imageCacheKey
    previousCacheKeyRef.current = imageCacheKey

    if (isNewImage) {
      setTransform(DEFAULT_TRANSFORM)
      if (loadedFlatImageKeys.has(imageCacheKey)) {
        setImageLoaded(true)
      } else {
        setImageLoaded(false)
      }
    }
  }, [imageCacheKey])

  useLayoutEffect(() => {
    const img = imageRef.current
    if (!img || !imageCacheKey) return
    if (img.complete && img.naturalWidth > 0) {
      loadedFlatImageKeys.add(imageCacheKey)
      setImageLoaded(true)
    }
  }, [imageUrl, imageCacheKey])

  const markImageLoaded = () => {
    if (imageCacheKey) {
      loadedFlatImageKeys.add(imageCacheKey)
    }
    setImageLoaded(true)
  }

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === panelRef.current)
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
    }
  }, [])

  const hasMultipleImages = flatImages.length > 1
  const canGoPrevious = hasMultipleImages && flatImageIndex > 0
  const canGoNext = hasMultipleImages && flatImageIndex < flatImages.length - 1

  const onPreviousImage = () => {
    if (!canGoPrevious || !onSelectFlatImageIndex) return
    onSelectFlatImageIndex(flatImageIndex - 1)
  }

  const onNextImage = () => {
    if (!canGoNext || !onSelectFlatImageIndex) return
    onSelectFlatImageIndex(flatImageIndex + 1)
  }

  let month = ''
  let day = 0
  let year = 0
  if (flatImage?.taken_on) {
    const taken = new Date(flatImage.taken_on)
    day = taken.getDate()
    month = monthNames[taken.getMonth()]
    year = taken.getFullYear()
  }

  const onWheel = (e: ReactWheelEvent) => {
    stopPanelEvent(e)
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setTransform(prev => ({
      ...prev,
      scale: clamp(prev.scale + delta, 0.25, 6),
    }))
  }

  const onViewportMouseDown = (e: ReactMouseEvent) => {
    stopPanelEvent(e)
    if (!imageUrl) return
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originOffsetX: transform.offsetX,
      originOffsetY: transform.offsetY,
    }
  }

  const onViewportMouseMove = (e: ReactMouseEvent) => {
    stopPanelEvent(e)
    if (!dragRef.current) return
    const dx = e.clientX - dragRef.current.startX
    const dy = e.clientY - dragRef.current.startY
    setTransform(prev => ({
      ...prev,
      offsetX: dragRef.current!.originOffsetX + dx,
      offsetY: dragRef.current!.originOffsetY + dy,
    }))
  }

  const onViewportMouseUp = (e: ReactMouseEvent) => {
    stopPanelEvent(e)
    dragRef.current = null
  }

  const resetView = () => setTransform(DEFAULT_TRANSFORM)

  const onToggleFullscreen = async () => {
    const panel = panelRef.current
    if (!panel) return

    if (document.fullscreenElement === panel) {
      await document.exitFullscreen()
      return
    }

    if (document.fullscreenElement) {
      await document.exitFullscreen()
    }

    await panel.requestFullscreen()
  }

  return (
    <>
      <PanelBase
        ref={panelRef}
        onMouseDown={stopPanelEvent}
        onMouseMove={stopPanelEvent}
        onMouseUp={stopPanelEvent}
        onClick={stopPanelEvent}
        onDoubleClick={stopPanelEvent}
        onWheel={stopPanelEvent}
        onTouchStart={stopPanelEvent}
      >
        <PanelHeader>
          <HeaderLeft>
            <Icon
              icon={iconCalendar}
              size={14}
              style={{ marginRight: '9px' }}
            />
            {month && day && year ? (
              <span>
                {month} {day}, {year}
              </span>
            ) : (
              <span>No capture date</span>
            )}
          </HeaderLeft>
          <HeaderActions>
            {hasMultipleImages && (
              <>
                <HeaderIconButton
                  type="button"
                  disabled={!canGoPrevious}
                  onClick={e => {
                    stopPanelEvent(e)
                    onPreviousImage()
                  }}
                  aria-label="Previous image"
                >
                  <img src={iconBackDark} alt="" />
                </HeaderIconButton>
                <HeaderIconButton
                  type="button"
                  disabled={!canGoNext}
                  onClick={e => {
                    stopPanelEvent(e)
                    onNextImage()
                  }}
                  aria-label="Next image"
                >
                  <img src={iconForward} alt="" />
                </HeaderIconButton>
              </>
            )}
            {imageUrl && (
              <HeaderIconButton
                type="button"
                onClick={e => {
                  stopPanelEvent(e)
                  void onToggleFullscreen()
                }}
                aria-label={
                  isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
                }
              >
                <img src={iconExpand} alt="" />
              </HeaderIconButton>
            )}
            <CloseButton
              type="button"
              onClick={e => {
                stopPanelEvent(e)
                onClose()
              }}
              aria-label="Close"
            >
              ×
            </CloseButton>
          </HeaderActions>
        </PanelHeader>

        {loading && !imageUrl && (
          <EmptyState>
            <LoadingIndicator />
          </EmptyState>
        )}

        {(!loading || imageUrl) && imageUrl && (
          <Viewport
            onWheel={onWheel}
            onMouseDown={onViewportMouseDown}
            onMouseMove={onViewportMouseMove}
            onMouseUp={onViewportMouseUp}
            onMouseLeave={onViewportMouseUp}
          >
            {!imageLoaded && (
              <ViewportLoadingOverlay>
                <LoadingIndicator fb size={28} />
              </ViewportLoadingOverlay>
            )}
            <ViewportImage
              ref={imageRef}
              src={imageUrl}
              alt="Viewpoint"
              draggable={false}
              onLoad={markImageLoaded}
              style={{
                transform: `translate(calc(-50% + ${transform.offsetX}px), calc(-50% + ${transform.offsetY}px)) scale(${transform.scale})`,
                opacity: imageLoaded ? 1 : 0,
              }}
            />
          </Viewport>
        )}

        {(!loading || !imageUrl) && !imageUrl && (
          <EmptyState>
            <span>No image available</span>
          </EmptyState>
        )}

        {(!loading || imageUrl) && (
          <PanelFooter>
            {imageUrl && (
              <FooterButton
                type="button"
                onClick={e => {
                  stopPanelEvent(e)
                  resetView()
                }}
              >
                Reset Zoom
              </FooterButton>
            )}
            <FooterButton
              type="button"
              onClick={e => {
                stopPanelEvent(e)
                setShowUploadModal(true)
              }}
            >
              Upload Image
            </FooterButton>
          </PanelFooter>
        )}
      </PanelBase>

      {showUploadModal && (
        <FlatImageUploadModal
          projectId={projectId}
          floorCode={floorCode}
          subPointId={subPointId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={onUploadSuccess}
        />
      )}
    </>
  )
}
